import React, { useState } from 'react';
import {
  FaCircle,
  FaExchangeAlt,
  FaHeadset,
  FaPhone,
  FaPhoneSlash
} from 'react-icons/fa';
import './style.css';
import userAgentSingleton from '../userAgent/userAgentSingleton';
import { SessionState } from 'sip.js';
import {
  MdHeadset,
  MdHeadsetOff,
  MdMic,
  MdMicOff,
  MdEdit,
  MdDeleteForever,
  MdSettings
} from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { FiLogOut, FiMonitor } from 'react-icons/fi';
import { verifyRoles } from 'components/Authentication/VerifyRoles';
import { debounce } from 'lodash';
import { usePlayDTMFSound } from '../../../../../hooks/usePlayDTMFSound';
import { IoMdKeypad, IoMdContact } from 'react-icons/io';
import { IoPersonAdd } from 'react-icons/io5';
import { FaHistory } from 'react-icons/fa';
import {
  HiPhoneIncoming,
  HiPhoneMissedCall,
  HiPhoneOutgoing
} from 'react-icons/hi';
import { useEffect } from 'react';
import CallHistoryService from '../services/CallHistoryService';
import moment from 'moment';
import ContactDrawer from '../drawer/ContactDrawer';
import SoftphoneContactsService from '../services/SoftphoneContactsService';
import { toast } from 'react-toastify';
import DialpadKeypad from '../dialpadKeyboard';

const debouncedPlaySound = debounce(
  (value, fn) => {
    fn(value);
  },
  300,
  { leading: true, trailing: false }
);

const DialpadWebRTC = ({
  ramal,
  onDisconnect,
  callState,
  callStateColor,
  callStateStatus,
  receivedInvitation,
  setOpen,
  onOpenAudioSettings
}) => {
  const [currentDialPadInput, setCurrentDialPadInput] = useState(
    userAgentSingleton.dialpadInput ?? ''
  );
  const [, forceUpdate] = useState({});

  const [isInCall, setIsInCall] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const history = useHistory();
  const { play } = usePlayDTMFSound();

  const handleCall = async (targetNumber) => {
    const numberToCall =
      typeof targetNumber === 'string' ? targetNumber : currentDialPadInput;
    if (!numberToCall) return;

    if (userAgentSingleton.isCallInProgress || isProcessing) return;

    setIsProcessing(true);
    setIsInCall(true);

    try {
      await userAgentSingleton.call(numberToCall);
    } catch (e) {
      setIsInCall(false);
    }

    setTimeout(() => {
      setIsProcessing(false);

      if (!userAgentSingleton.isCallInProgress) {
        setIsInCall(false);
      }
    }, 10000);
  };
  const handleEndCall = () => {
    if (isProcessing) return;

    setIsProcessing(true);
    userAgentSingleton.endCall();

    setTimeout(() => setIsProcessing(false), 5000);
  };

  const getButtonStyle = () => ({
    opacity: isProcessing ? 0.5 : 1,
    cursor: isProcessing ? 'not-allowed' : 'pointer'
  });

  const handleKeypadChange = (newValue) => {
    const oldValue = currentDialPadInput;
    setCurrentDialPadInput(newValue);

    if (newValue.length > oldValue.length) {
      const typedChar = newValue.slice(-1);

      if ('0123456789*#'.includes(typedChar)) {
        if (!userAgentSingleton.defean || !userAgentSingleton.mute) {
          debouncedPlaySound(typedChar, play);
        }

        if (userAgentSingleton.session != undefined) {
          userAgentSingleton.sendDTMF(typedChar);
        }
      }
    }
  };

  const [viewMode, setViewMode] = useState('dialpad');

  const [callHistory, setCallHistory] = useState([]);

  const [limit] = useState(10);

  const offsetRef = React.useRef(0);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = React.useRef(null);

  useEffect(() => {
    if (viewMode === 'history') {
      setHasMore(true);
      offsetRef.current = 0;
      fetchCallHistory(true);
    }
  }, [ramal, viewMode]);

  // Monitora mudanças no callState para atualizar o estado do botão
  useEffect(() => {
    const isActiveCall =
      callState !== SessionState.Initial && callState !== undefined;
    const hasActiveSession =
      userAgentSingleton.session && callState !== SessionState.Initial;

    setIsInCall(isActiveCall || hasActiveSession);
    setIsProcessing(false);
  }, [callState]);

  const formatCallDate = (dateString) => {
    const callDate = moment(dateString);
    const now = moment();

    if (callDate.isSame(now, 'day')) {
      return callDate.format(' HH:mm');
    } else if (callDate.isSame(now, 'week')) {
      return callDate.format(' dddd');
    } else {
      return callDate.format(' DD/MM/YYYY');
    }
  };

  const fetchCallHistory = async (reset = false) => {
    try {
      const currentOffset = reset ? 0 : offsetRef.current;
      const response = await CallHistoryService.getHistoryByRamal(
        ramal,
        limit,
        currentOffset
      );
      const historyData = Array.isArray(response.data) ? response.data : [];
      if (reset) {
        setCallHistory(historyData);
      } else {
        setCallHistory((prev) => [...prev, ...historyData]);
      }
      setHasMore(historyData.length === limit);
      if (reset) {
        offsetRef.current = limit;
      } else {
        offsetRef.current += limit;
      }
    } catch (error) {
      if (reset) setCallHistory([]);
    }
  };

  useEffect(() => {
    if (!hasMore || viewMode !== 'history') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchCallHistory();
        }
      },
      {
        root: document.querySelector('.historyPanel'),
        rootMargin: '0px',
        threshold: 1.0
      }
    );

    const loader = loaderRef.current;
    if (loader) observer.observe(loader);

    return () => {
      if (loader) observer.unobserve(loader);
    };
  }, [hasMore, viewMode]);

  const [contacts, setContacts] = useState([]);

  const fetchContacts = async () => {
    try {
      const res = await SoftphoneContactsService.getSoftphoneContactsList();
      const fetched = res.data?.data || [];
      const transformed = fetched.map((c) => ({
        id: c.id,
        nome: c.name,
        numero: c.number
      }));
      setContacts(transformed);
    } catch (err) {
      console.error('Erro ao buscar contatos softphone:', err);
      setContacts([]);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const syncReorder = async (orderedContacts) => {
    try {
      const contactsToSend = orderedContacts.map((c, index) => ({
        id: c.id,
        position: index + 1
      }));

      await SoftphoneContactsService.reorderContactList(contactsToSend);
    } catch (err) {
      console.error('Erro ao reordenar contatos:', err);
    }
  };

  const handleDrop = (dropIndex) => {
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const updatedContacts = [...contacts];
    const [moved] = updatedContacts.splice(draggedIndex, 1);
    updatedContacts.splice(dropIndex, 0, moved);

    setContacts(updatedContacts);
    setDraggedIndex(null);
    setDragOverIndex(null);

    syncReorder(updatedContacts);
  };

  const handleTransfer = (numero) => {
    setSelectedContact({ nome: '', numero });
    setDrawerType('transfer');
    setDrawerOpen(true);
  };

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    // Anteriormente o evento de unload só era invocado caso o state isInCall fosse verdadeiro
    // Essa checagem foi modificada para que rode sempre que a página desmontar, por qualquer motivo (https://app.clickup.com/t/86ae77hyp)
    const onBeforeUnload = () => {
      handleEndCall();
    };

    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  return (
    <div className="dialpadModal">
      <div className="ramalRow">
        <div className="ramalInfo">
          <FaHeadset
            strokeWidth={2}
            size={'1rem'}
            style={{
              cursor: 'pointer',
              borderRadius: '5%',
              fontWeight: 'bold',
              fontSize: '1.4rem',
              marginRight: '6px'
            }}
          />
          <p>{ramal}</p>
        </div>
        <button
          className="addContactBtn"
          onClick={() => {
            setSelectedContact({ nome: '', numero: '' });
            setDrawerType('add');
            setDrawerOpen(true);
          }}
          title="Adicionar novo contato"
        >
          <IoPersonAdd style={{ borderRadius: '5%', marginRight: '6px' }} />
          Adicionar contato
        </button>
      </div>
      {userAgentSingleton.session?._state != 'Established' &&
      localStorage.getItem('webrtc_auto_login') === 'false' ? (
        <div className="disconnectButton" onClick={onDisconnect}>
          <FiLogOut />
          <p>Desconectar</p>
        </div>
      ) : userAgentSingleton.session?._state == 'Established' ? (
        <div
          className={`transferButton ${
            !userAgentSingleton.session ? 'disabled' : ''
          }`}
          onClick={() => {
            if (userAgentSingleton.session) {
              setSelectedContact({ nome: '', numero: '' });
              setDrawerType('transfer');
              setDrawerOpen(true);
            }
          }}
          style={{
            cursor: userAgentSingleton.session ? 'pointer' : 'not-allowed'
          }}
        >
          <FaExchangeAlt />
          <p>Transferir</p>
        </div>
      ) : null}
      {receivedInvitation == undefined ? (
        viewMode === 'dialpad' ? (
          <div className="numPan">
            <DialpadKeypad
              value={currentDialPadInput}
              onChange={handleKeypadChange}
              onEnter={() => handleCall(currentDialPadInput)}
            />
            {!isInCall ? (
              <div className="call">
                <div
                  className={`call-button ${isProcessing ? 'disabled' : ''}`}
                  onClick={handleCall}
                  style={getButtonStyle()}
                >
                  <FaPhone />
                </div>
              </div>
            ) : (
              <div className="endCall">
                <div
                  className={`end-call-button ${
                    isProcessing ? 'disabled' : ''
                  }`}
                  onClick={handleEndCall}
                  style={getButtonStyle()}
                >
                  <FaPhoneSlash />
                </div>
              </div>
            )}
          </div>
        ) : viewMode === 'history' ? (
          <div className="historyPanel">
            <ul>
              {callHistory.length > 0 ? (
                <>
                  {callHistory.map((call, index) => (
                    <li key={index}>
                      <span className="call-icon">
                        {call.disposition === 'NO ANSWER' ? (
                          <HiPhoneMissedCall style={{ color: 'red' }} />
                        ) : call.call_type === 'ENTRADA' ? (
                          <HiPhoneIncoming style={{ color: 'green' }} />
                        ) : (
                          <HiPhoneOutgoing style={{ color: 'green' }} />
                        )}
                      </span>
                      <div className="call-info">
                        <p className="call-name">{call.nome}</p>
                        <p className="call-number">{call.numero}</p>
                      </div>
                      <p className="call-date">
                        {formatCallDate(call.calldate_start)}
                      </p>
                    </li>
                  ))}
                  <div ref={loaderRef} style={{ height: '1px' }} />
                </>
              ) : (
                <p>Nenhum histórico encontrado.</p>
              )}
            </ul>
          </div>
        ) : viewMode === 'contacts' ? (
          <div className="contactsPanel">
            <div className="contactsHeader">
              <span>Nome</span>
              <span>Número</span>
              <span
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center'
                }}
              >
                Ações
              </span>
            </div>
            <ul className="contactsList">
              {contacts.map((contact, index) => (
                <li
                  key={contact.id}
                  draggable
                  onDragStart={() => setDraggedIndex(index)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOverIndex(index);
                  }}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDrop={() => handleDrop(index)}
                  onDragEnd={() => {
                    setDraggedIndex(null);
                    setDragOverIndex(null);
                  }}
                  className={
                    draggedIndex === index
                      ? 'dragging'
                      : dragOverIndex === index
                      ? 'drag-over'
                      : ''
                  }
                >
                  <div className="contactName">{contact.nome}</div>
                  <div className="contactNumber">{contact.numero}</div>
                  <div className="contactActions">
                    <button
                      title="Fazer chamada"
                      onClick={() => handleCall(contact.numero)}
                      className="callBtn"
                    >
                      <FaPhone style={{ color: 'green' }} />
                    </button>
                    {userAgentSingleton.session?.state ===
                    SessionState.Established ? (
                      <button
                        title="Transferir contato"
                        onClick={() => handleTransfer(contact.numero)}
                        className="transferBtn"
                      >
                        <FaExchangeAlt style={{ color: 'blue' }} />
                      </button>
                    ) : (
                      <button
                        title="Transferência indisponível (sem chamada)"
                        className="transferBtn"
                        disabled
                        style={{ cursor: 'not-allowed', opacity: 0.4 }}
                      >
                        <FaExchangeAlt />
                      </button>
                    )}

                    <button
                      title="Editar contato"
                      onClick={() => {
                        setSelectedContact(contact);
                        setDrawerType('edit');
                        setDrawerOpen(true);
                      }}
                      className="editBtn"
                    >
                      <MdEdit />
                    </button>
                    <button
                      title="Deletar contato"
                      onClick={() => {
                        setSelectedContact(contact);
                        setDrawerType('delete');
                        setDrawerOpen(true);
                      }}
                      className="deleteBtn"
                    >
                      <MdDeleteForever style={{ color: 'red' }} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null
      ) : (
        <div className="receivedInvitation">
          <div>
            <p>{userAgentSingleton.getSessionDisplayName()}</p>
            <p>{userAgentSingleton.getSessionNumber()}</p>
          </div>
          <div className="receivedInvitationActions">
            <div className="call" onClick={() => receivedInvitation.accept()}>
              <div>
                <FaPhone />
              </div>
            </div>
            <div
              className="endCall"
              onClick={() => {
                userAgentSingleton.setLostSessionAsFalse();
                receivedInvitation.reject();
              }}
            >
              <div>
                <FaPhoneSlash />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="dialpad-menu">
        <div
          className="menuButton"
          onClick={() => {
            setViewMode('history');
          }}
        >
          <FaHistory />
          <p>Histórico</p>
        </div>

        <div className="menuButton" onClick={() => setViewMode('dialpad')}>
          <IoMdKeypad />
          <p>Teclado</p>
        </div>
        <div className="menuButton" onClick={() => setViewMode('contacts')}>
          <IoMdContact />
          <p>Contatos</p>
        </div>
      </div>
      <div className="dialpad-toolbar">
        <div style={{ display: 'flex ' }}>
          {verifyRoles(['MONITORING_PBXIP']) ? (
            <div
              className="monitor"
              onClick={() => {
                setOpen(false);
                history.push('/admin/monitoring/extensions');
              }}
            >
              <div>
                <FiMonitor size={22} title="Ir para monitoramento de ramais" />
              </div>
            </div>
          ) : null}

          <div className="statusRow">
            <div
              style={{
                color: callStateColor
              }}
            >
              <FaCircle size={10} />
            </div>
            <p>{`${callStateStatus ?? 'Desconectado'} ${
              userAgentSingleton.session != undefined &&
              userAgentSingleton.session.state != SessionState.Initial
                ? `- ${userAgentSingleton.getSessionNumber()}`
                : ''
            }`}</p>
          </div>
        </div>
        <div className="dialpad-audio-toolbar">
          <div
            className="mute"
            onClick={() => {
              userAgentSingleton.toggleMute();
              forceUpdate({});
            }}
          >
            <div>
              {userAgentSingleton.mute ? (
                <MdMicOff size={22} />
              ) : (
                <MdMic size={22} />
              )}
            </div>
          </div>

          <div
            className="defean"
            onClick={() => {
              userAgentSingleton.toggleDefean();
              userAgentSingleton.toggleMute(userAgentSingleton.defean);
              forceUpdate({});
            }}
          >
            <div>
              {userAgentSingleton.defean ? (
                <MdHeadsetOff size={22} />
              ) : (
                <MdHeadset size={22} />
              )}
            </div>
          </div>

          <div
            onClick={() => {
              if (onOpenAudioSettings) onOpenAudioSettings();
            }}
            title="Configurações de Dispositivos de Áudio"
          >
            <div>
              <MdSettings size={22} />
            </div>
          </div>
        </div>
      </div>
      <ContactDrawer
        isOpen={drawerOpen}
        type={drawerType}
        contactData={selectedContact}
        onClose={() => setDrawerOpen(false)}
        onSubmit={(data) => {
          if (drawerType === 'edit') {
            SoftphoneContactsService.updateSoftphoneContact(
              {
                name: data.nome,
                number: data.numero
              },
              data.id
            )
              .then(() => fetchContacts())
              .catch((err) => console.error('Erro ao atualizar contato:', err))
              .finally(() => setDrawerOpen(false));
          } else if (drawerType === 'add') {
            SoftphoneContactsService.createSoftphoneContact({
              name: data.nome,
              number: data.numero
            })
              .then(() => {
                fetchContacts();
                setDrawerOpen(false);
              })
              .catch((err) => {
                toast.error(
                  err.response?.data?.message || 'Erro ao criar contato.'
                );
              });
          } else if (drawerType === 'delete') {
            SoftphoneContactsService.deleteSoftphoneContact(data.id)
              .then(() => fetchContacts())
              .catch((err) => console.error('Erro ao deletar contato:', err))
              .finally(() => setDrawerOpen(false));
          }
        }}
      />
    </div>
  );
};

export default DialpadWebRTC;
